import ScrollModal from "@/components/common/scroll-modal";
import TextField from "@/components/common/text-field";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import {
  Button,
  Heading,
  Icon,
  GroupTextField,
  Radiobutton,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import TextFieldID from "@/pages/digital-files/entity/components/TextFieldID";
import SwitchField from "@/workflow/components/configurations/form/components/SwitchField";
import InfoMessage from "@/workflow/components/configurations/form/components/InfoMessage";
import { DateInput } from "@/workflow/components/configurations/form/components/DateInput";
import TimeInput from "@/workflow/components/configurations/form/components/TimeInput";
import dayjs from "dayjs";
import { useConfigModal } from "../hooks/useConfigModal";
import type { EntityField } from "../../types";
import { DataCenterField } from "../components/DataCenterField";
import { getDateFromString } from "@/utils/dates";

interface DateTimeFieldConfigModalProps {
  isOpen: boolean;
  isCreateMode?: boolean;
  formData?: EntityField;
  onSave: (data: EntityField) => void;
  onClose: () => void;
}

export const DateTimeFieldConfigModal = ({
  isOpen,
  isCreateMode = true,
  formData,
  onSave,
  onClose,
}: DateTimeFieldConfigModalProps) => {
  const [localListNewItem, setLocalListNewItem] = useState("");
  const {
    fieldKey,
    fieldName,
    fieldNameError,
    localListItems,
    hasDropdown,
    buttonPosition,
    valuesSource,
    expandSelectedRecord,
    minDate,
    maxDate,
    format24,
    includeTime,
    defaultDate,
    defaultTime,
    valuesColumnName,
    valuesTableName,
    setFieldKey,
    setFieldName,
    setLocalListItems,
    setHasDropdown,
    setButtonPosition,
    setValuesSource,
    setExpandSelectedRecord,
    setMinDate,
    setMaxDate,
    setFormat24,
    setIncludeTime,
    setDefaultDate,
    setDefaultTime,
    setDataCenterValue,
    getConfigData,
    isValid,
    isNew,
  } = useConfigModal("date", formData);

  const handleSave = () => {
    const values = getConfigData();
    onSave(values);
    onClose();
  };

  const handleAddLocalItem = () => {
    const v = localListNewItem.trim();
    if (!v) return;
    setLocalListItems((prev) => [...prev, v]);
    setLocalListNewItem("");
  };

  const handleRemoveLocalItem = (index: number) => {
    setLocalListItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMinDateChange = (date: string) => {
    if (date) {
      setMinDate(dayjs(date).format("DD/MM/YYYY"));
    } else {
      setMinDate("");
    }
  };

  const handleMaxDateChange = (date: string) => {
    if (date) {
      setMaxDate(dayjs(date).format("DD/MM/YYYY"));
    } else {
      setMaxDate("");
    }
  };

  const isInvalidRange = useMemo(() => {
    return Boolean(
      minDate &&
      maxDate &&
      dayjs(minDate, "DD/MM/YYYY").isAfter(dayjs(maxDate, "DD/MM/YYYY")),
    );
  }, [minDate, maxDate]);

  const isDefaultDateInvalid = useMemo(() => {
    if (!defaultDate?.trim()) return false;

    const parsedDefault = dayjs(defaultDate, "DD/MM/YYYY", true);

    // Verificar que tenga formato válido
    if (!parsedDefault.isValid()) return true;

    // Verificar que esté dentro del rango
    if (minDate) {
      const parsedMin = dayjs(minDate, "DD/MM/YYYY");
      if (parsedDefault.isBefore(parsedMin, "day")) return true;
    }

    if (maxDate) {
      const parsedMax = dayjs(maxDate, "DD/MM/YYYY");
      if (parsedDefault.isAfter(parsedMax, "day")) return true;
    }

    return false;
  }, [defaultDate, minDate, maxDate]);

  const defaultDateErrorMessage = useMemo(() => {
    if (!isDefaultDateInvalid) return undefined;

    if (minDate || maxDate) {
      return "La fecha por defecto debe estar dentro del rango especificado.";
    }

    return "La fecha por defecto tiene un formato inválido.";
  }, [isDefaultDateInvalid, minDate, maxDate]);

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col gap-0 text-size-body-sm"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <Heading variant="h5">Fecha y hora</Heading>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="px-8 py-3">
          <div className="flex gap-3">
            <Button variant="tertiary" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="default"
              className="flex-1"
              disabled={!isValid || isInvalidRange || isDefaultDateInvalid}
              onClick={handleSave}
            >
              Guardar
            </Button>
          </div>
        </div>
      }
    >
      <div className="p-8 flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Detalles
          </Heading>

          <TextFieldID
            label="Nombre del campo"
            required
            value={fieldName}
            onChange={setFieldName}
            keyValue={fieldKey}
            onKeyChange={setFieldKey}
            canChangeKey={isCreateMode || isNew}
            error={fieldNameError}
          />
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Opciones
          </Heading>

          <SwitchField
            id="datetime-field-dropdown"
            label="¿Agregar botón dropdown al campo?"
            required
            checked={hasDropdown}
            onChange={setHasDropdown}
            isTrue="Sí"
            isFalse="No"
          />

          {hasDropdown && (
            <>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-body-loud text-sm leading-2xl">
                  Posición del botón{" "}
                  <span className="text-sm text-danger">*</span>
                </label>
                <DropdownList
                  options={[
                    { value: "left", label: "Izquierda" },
                    { value: "right", label: "Derecha" },
                  ]}
                  placeholder="Seleccionar"
                  selectedValue={{
                    value: buttonPosition,
                    label: buttonPosition === "left" ? "Izquierda" : "Derecha",
                  }}
                  toggleSelection={(item) => {
                    if (item.value === "left" || item.value === "right") {
                      setButtonPosition(item.value);
                    }
                  }}
                  widthClassName="w-full"
                  scrollClassName="h-auto"
                />
              </div>

              <div className="flex flex-col gap-1">
                <div className="font-semibold text-body-loud text-sm leading-2xl">
                  ¿De donde provienen los valores?{" "}
                  <span className="text-sm text-danger">*</span>
                </div>
                <div className="flex flex-col gap-2 mt-1">
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="datetime-field-source-database"
                      name="datetime-field-source"
                      value="database"
                      checked={valuesSource === "database"}
                      onChange={() => setValuesSource("database")}
                    />
                    <label
                      htmlFor="datetime-field-source-database"
                      className="text-body text-sm"
                    >
                      Desde centro de datos
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="datetime-field-source-manual"
                      name="datetime-field-source"
                      value="manual"
                      checked={valuesSource === "manual"}
                      onChange={() => setValuesSource("manual")}
                    />
                    <label
                      htmlFor="datetime-field-source-manual"
                      className="text-body text-sm"
                    >
                      Locales
                    </label>
                  </div>
                </div>
              </div>

              {valuesSource === "manual" && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end group">
                    <div className="w-full">
                      <TextField
                        placeholder="Añade un nuevo item"
                        className="rounded-tr-none rounded-br-none border-r-0"
                        label="Lista local"
                        required
                        value={localListNewItem}
                        onChange={(e) => setLocalListNewItem(e.target.value)}
                      />
                    </div>
                    <Button
                      className="h-11 rounded-bl-none rounded-tl-none border-l-0 group-hover:border-neutral-loud group-focus-within:border-contextual-action! group-hover:bg-neutral-object-accent-silent group-focus-within:bg-neutral-object-accent-quiet!"
                      variant="quiet"
                      type="button"
                      onClick={handleAddLocalItem}
                      aria-label="Agregar item"
                    >
                      <Icon name="plus" type="far" />
                    </Button>
                  </div>

                  <InfoMessage>
                    El primer valor de la lista será el pre-seleccionado por
                    default
                  </InfoMessage>

                  {localListItems.length > 0 && (
                    <div className="w-full rounded-lg border border-neutral bg-neutral-object shadow-sm p-1">
                      <div className="flex flex-col">
                        {localListItems.map((item, index) => (
                          <div
                            key={`${item}-${index}`}
                            className="px-3 py-2 flex items-center gap-3"
                          >
                            <div className="flex-1 text-body text-sm truncate">
                              {item}
                            </div>
                            <Button
                              variant="quiet"
                              size="icon"
                              type="button"
                              aria-label="Eliminar"
                              onClick={() => handleRemoveLocalItem(index)}
                              className="w-8 h-8"
                            >
                              <Icon type="far" name="times" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {valuesSource === "database" && (
                <DataCenterField
                  required
                  fieldKey="valueDatacenter"
                  selectedTable={valuesTableName}
                  selectedColumn={valuesColumnName}
                  onChange={setDataCenterValue}
                />
              )}
              {valuesSource === "database" && (
                <SwitchField
                  id="datetime-field-expand-selected"
                  label="Expandir registro seleccionado"
                  required
                  checked={expandSelectedRecord}
                  onChange={setExpandSelectedRecord}
                  isTrue="Sí, expandir"
                  isFalse="No, generar una sola variable (array)"
                />
              )}
            </>
          )}
        </div>

        <div className="border-t border-neutral-quiet" />

        <div className="flex flex-col gap-4">
          <Heading variant="h6" className="mb-0!" color="loud">
            Configuración
          </Heading>

          <div>
            <div className="flex gap-4">
              <div className="flex flex-col flex-1">
                <label
                  htmlFor="fechaMinima"
                  className="mb-1 text-sm font-medium text-body-loud"
                >
                  Fecha mínima
                </label>
                <DateInput
                  id="fechaMinima"
                  maxDate={getDateFromString(maxDate)}
                  onChangeDate={handleMinDateChange}
                  value={minDate}
                />
              </div>
              <div className="flex flex-col flex-1">
                <label
                  htmlFor="fechaMaxima"
                  className="mb-1 text-sm font-medium text-body-loud"
                >
                  Fecha máxima
                </label>
                <DateInput
                  id="fechaMaxima"
                  minDate={getDateFromString(minDate)}
                  onChangeDate={handleMaxDateChange}
                  value={maxDate}
                />
              </div>
            </div>

            <InfoMessage
              className={`mt-1 ${isInvalidRange ? "text-danger!" : ""}`}
            >
              {isInvalidRange
                ? "La fecha mínima no puede ser mayor que la fecha máxima."
                : "Si dejas estos campos vacíos, no se aplicarán límites."}
            </InfoMessage>
          </div>

          <SwitchField
            id="datetime-field-include-time"
            label="¿Incluir tiempo?"
            required
            checked={includeTime}
            onChange={(v) => {
              setIncludeTime(v);
              if (!v) {
                setDefaultTime("");
              }
            }}
            isTrue="Sí"
            isFalse="No"
          />

          {includeTime && (
            <SwitchField
              id="datetime-field-24h"
              label="¿Formato 24h?"
              required={includeTime}
              checked={format24}
              onChange={(checked) => {
                setDefaultTime("");
                setFormat24(checked);
              }}
              isTrue="Sí"
              isFalse="No"
            />
          )}

          <div>
            <GroupTextField
              label="Valor por defecto"
              labelHelperText="Opcional"
              error={defaultDateErrorMessage}
            >
              <div className="w-full rounded-lg border border-neutral shadow-sm overflow-hidden group">
                <div className="flex w-full">
                  <div
                    className={`flex-1 ${includeTime ? "border-r border-neutral" : ""}`}
                  >
                    <DateInput
                      minDate={getDateFromString(minDate)}
                      maxDate={getDateFromString(maxDate)}
                      onChangeDate={(date) => {
                        if (date) {
                          setDefaultDate(dayjs(date).format("DD/MM/YYYY"));
                        } else {
                          setDefaultDate("");
                        }
                      }}
                      value={defaultDate}
                      className="bg-neutral-object-accent-silent/5! h-11! border-0! -roundednone!"
                      wrapperClassName="h-11"
                    />
                  </div>

                  {includeTime && (
                    <div className="flex-1">
                      <TimeInput
                        value={defaultTime}
                        shouldUse24={format24}
                        onChange={(val) => setDefaultTime(val)}
                        className="bg-neutral-object-accent-silent/5! h-11! border-0! rounded-none!"
                        wrapperClassName="h-11"
                      />
                    </div>
                  )}
                </div>
              </div>
            </GroupTextField>

            <InfoMessage className="mt-1">
              Si está vacío no se aplicará ningún valor por defecto.
            </InfoMessage>
          </div>
        </div>
      </div>
    </ScrollModal>
  );
};
